import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BsFilePdf, BsDownload, BsTypeBold, BsTypeItalic, BsTypeUnderline, BsListUl, BsListOl, BsTextLeft, BsTextCenter, BsTextRight } from 'react-icons/bs';
import { jsPDF } from 'jspdf';
import SEO from '../components/SEO';
import { textToPdfSchemas } from '../schemas';
import './TextToPDF.css';

const TextToPDF = () => {
  const [content, setContent] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [fontColor, setFontColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [isGenerating, setIsGenerating] = useState(false);
  const editorRef = useRef(null);

  const applyStyle = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    setFontSize(size);
    applyStyle('fontSize', '7'); // fontSize uses 1-7 scale
    // Apply custom size via CSS
    document.execCommand('foreColor', false, fontColor);
  };

  const handleColorChange = (e) => {
    const color = e.target.value;
    setFontColor(color);
    applyStyle('foreColor', color);
  };

  const handleGeneratePDF = async () => {
    if (!content && !editorRef.current?.innerHTML) {
      alert('Please add some content first!');
      return;
    }

    setIsGenerating(true);

    try {
      // Start timer for minimum 3 seconds
      const startTime = Date.now();

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - (margin * 2);
      
      // Get HTML content from editor
      const htmlContent = editorRef.current.innerHTML;
      
      // Parse HTML and convert to PDF
      // For simplicity, we'll extract text content with basic formatting
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      let yPosition = margin;
      const lineHeight = 7;
      
      // Set background color
      if (backgroundColor !== '#ffffff') {
        pdf.setFillColor(backgroundColor);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      }

      // Process each element
      const processNode = (node, currentFontSize = 16, isBold = false, isItalic = false) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            // Set font style
            let fontStyle = 'normal';
            if (isBold && isItalic) fontStyle = 'bolditalic';
            else if (isBold) fontStyle = 'bold';
            else if (isItalic) fontStyle = 'italic';
            
            pdf.setFont('helvetica', fontStyle);
            pdf.setFontSize(currentFontSize);
            pdf.setTextColor(fontColor);
            
            const lines = pdf.splitTextToSize(text, maxWidth);
            lines.forEach(line => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              pdf.text(line, margin, yPosition);
              yPosition += lineHeight;
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          let newFontSize = currentFontSize;
          let newBold = isBold;
          let newItalic = isItalic;
          
          // Handle different HTML elements
          switch(tagName) {
            case 'h1':
              newFontSize = 24;
              newBold = true;
              yPosition += 5;
              break;
            case 'h2':
              newFontSize = 20;
              newBold = true;
              yPosition += 4;
              break;
            case 'h3':
              newFontSize = 18;
              newBold = true;
              yPosition += 3;
              break;
            case 'strong':
            case 'b':
              newBold = true;
              break;
            case 'em':
            case 'i':
              newItalic = true;
              break;
            case 'br':
              yPosition += lineHeight;
              return;
            case 'div':
            case 'p':
              yPosition += 3;
              break;
            case 'ul':
            case 'ol':
              yPosition += 2;
              break;
            case 'li':
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(currentFontSize);
              pdf.text('•', margin, yPosition);
              break;
          }
          
          // Process children
          Array.from(node.childNodes).forEach(child => {
            processNode(child, newFontSize, newBold, newItalic);
          });
          
          // Add spacing after block elements
          if (['p', 'div', 'h1', 'h2', 'h3', 'ul', 'ol'].includes(tagName)) {
            yPosition += 2;
          }
        }
      };
      
      // Process all nodes
      Array.from(tempDiv.childNodes).forEach(node => {
        processNode(node);
      });

      // Ensure minimum 3 seconds
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(3000 - elapsedTime, 0);
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      // Save PDF
      pdf.save('document.pdf');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="text-to-pdf-page">
      <SEO 
        title="Text to PDF Converter – Convert TXT to PDF Free"
        description="Convert plain text to a professional PDF document online free. Fast text-to-PDF conversion with no formatting hassle."
        keywords="text to pdf, convert txt to pdf, text to pdf converter"
        path="/text-to-pdf"
        schema={textToPdfSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsFilePdf />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Text to PDF
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Create beautiful PDF documents from text with rich formatting
          </motion.p>
        </div>
      </section>

      <section className="editor-section">
        <div className="editor-container">
          <motion.div
            className="editor-card"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {/* Toolbar */}
            <div className="editor-toolbar">
              <div className="toolbar-group">
                <label className="toolbar-label">Font Size:</label>
                <select 
                  className="font-size-select"
                  value={fontSize}
                  onChange={handleFontSizeChange}
                >
                  <option value="12">12</option>
                  <option value="14">14</option>
                  <option value="16">16</option>
                  <option value="18">18</option>
                  <option value="20">20</option>
                  <option value="24">24</option>
                  <option value="28">28</option>
                  <option value="32">32</option>
                </select>
              </div>

              <div className="toolbar-divider" />

              <div className="toolbar-group">
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('bold')}
                  title="Bold (Ctrl+B)"
                >
                  <BsTypeBold />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('italic')}
                  title="Italic (Ctrl+I)"
                >
                  <BsTypeItalic />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('underline')}
                  title="Underline (Ctrl+U)"
                >
                  <BsTypeUnderline />
                </button>
              </div>

              <div className="toolbar-divider" />

              <div className="toolbar-group">
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('justifyLeft')}
                  title="Align Left"
                >
                  <BsTextLeft />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('justifyCenter')}
                  title="Align Center"
                >
                  <BsTextCenter />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('justifyRight')}
                  title="Align Right"
                >
                  <BsTextRight />
                </button>
              </div>

              <div className="toolbar-divider" />

              <div className="toolbar-group">
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('insertUnorderedList')}
                  title="Bullet List"
                >
                  <BsListUl />
                </button>
                <button 
                  className="toolbar-btn"
                  onClick={() => applyStyle('insertOrderedList')}
                  title="Numbered List"
                >
                  <BsListOl />
                </button>
              </div>

              <div className="toolbar-divider" />

              <div className="toolbar-group">
                <label className="toolbar-label">Headings:</label>
                <select 
                  className="heading-select"
                  onChange={(e) => applyStyle('formatBlock', e.target.value)}
                  defaultValue=""
                >
                  <option value="">Normal</option>
                  <option value="h1">Heading 1</option>
                  <option value="h2">Heading 2</option>
                  <option value="h3">Heading 3</option>
                </select>
              </div>

              <div className="toolbar-divider" />

              <div className="toolbar-group">
                <label className="toolbar-label">Text Color:</label>
                <input 
                  type="color"
                  className="color-picker"
                  value={fontColor}
                  onChange={handleColorChange}
                />
              </div>

              <div className="toolbar-group">
                <label className="toolbar-label">Background:</label>
                <input 
                  type="color"
                  className="color-picker"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                />
              </div>
            </div>

            {/* Editor */}
            <div 
              ref={editorRef}
              className="text-editor"
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => setContent(e.currentTarget.innerHTML)}
              style={{ 
                fontSize: `${fontSize}px`,
                backgroundColor: backgroundColor
              }}
              data-placeholder="Start typing your content here..."
            />

            {/* Generate Button */}
            <div className="editor-actions">
              <motion.button
                className="btn-generate"
                onClick={handleGeneratePDF}
                disabled={isGenerating}
                whileHover={{ scale: isGenerating ? 1 : 1.02 }}
                whileTap={{ scale: isGenerating ? 1 : 0.98 }}
                animate={isGenerating ? {
                  boxShadow: [
                    '0 4px 20px var(--primary-shadow)',
                    '0 6px 30px var(--primary-shadow)',
                    '0 4px 20px var(--primary-shadow)'
                  ]
                } : {}}
                transition={{ duration: 1, repeat: isGenerating ? Infinity : 0 }}
              >
                {isGenerating ? (
                  <>
                    <BsDownload />
                    Generating<span className="dots">...</span>
                  </>
                ) : (
                  <>
                    <BsDownload />
                    Generate PDF
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TextToPDF;
